import {
  createContext,
  useContext,
  useReducer,
  useRef,
  useCallback,
  type ReactNode,
  type RefObject,
} from "react";
import {
  MODES,
  INITIAL_CALIBRATION_POINTS,
  type CalibrationMode,
  type CalibrationPoints,
  type Point,
} from "../utils/constants";

const CORNER_ORDER = ["tl", "tr", "br", "bl"] as const;

interface CalibrationState {
  isSurveyLoaded: boolean;
  surveyType: "image" | "pdf" | null;
  surveyData: string | ArrayBuffer | null;
  surveyFile: File | null;
  currentMode: CalibrationMode;
  calibPoints: CalibrationPoints;
  scaleFactor: number | null;
  isCalibrationComplete: boolean;
  nextCornerIndex: number;
  allCornersMarked: boolean;
  shedPosition: Point;
  shedRotation: number;
  isShedLocked: boolean;
  isShedPlaced: boolean;
  isDragging: boolean;
  isRotating: boolean;
}

type CalibrationAction =
  | { type: "LOAD_SURVEY"; surveyType: "image" | "pdf"; surveyData: string | ArrayBuffer; file: File }
  | { type: "CLEAR_SURVEY" }
  | { type: "SET_MODE"; mode: CalibrationMode }
  | { type: "START_CALIBRATION" }
  | { type: "START_MARK_CORNERS" }
  | { type: "ADD_CORNER_POINT"; point: Point }
  | { type: "SET_CALIB_POINT"; position: string; point: Point }
  | { type: "SET_ALL_CALIB_POINTS"; points: CalibrationPoints }
  | { type: "COMPLETE_CALIBRATION"; scaleFactor: number }
  | { type: "START_SHED_PLACEMENT" }
  | { type: "SET_SHED_POSITION"; position: Point }
  | { type: "SET_SHED_ROTATION"; rotation: number }
  | { type: "LOCK_SHED" }
  | { type: "UNLOCK_SHED" }
  | { type: "SET_DRAGGING"; value: boolean }
  | { type: "SET_ROTATING"; value: boolean }
  | { type: "RESET_CALIBRATION" };

export interface CalibrationContextValue extends CalibrationState {
  imageViewerRef: RefObject<HTMLImageElement | null>;
  pdfViewerRef: RefObject<HTMLCanvasElement | null>;
  surveyContainerRef: RefObject<HTMLDivElement | null>;
  calibrationSvgRef: RefObject<SVGSVGElement | null>;
  shedOverlayRef: RefObject<HTMLDivElement | null>;
  cornerOrder: readonly string[];
  getNextCornerName: () => string;
  loadSurvey: (surveyType: "image" | "pdf", surveyData: string | ArrayBuffer, file: File) => void;
  clearSurvey: () => void;
  setMode: (mode: CalibrationMode) => void;
  startCalibration: () => void;
  startMarkCorners: () => void;
  addCornerPoint: (point: Point) => void;
  setCalibPoint: (position: string, point: Point) => void;
  setAllCalibPoints: (points: CalibrationPoints) => void;
  completeCalibration: (scaleFactor: number) => void;
  startShedPlacement: () => void;
  setShedPosition: (position: Point) => void;
  setShedRotation: (rotation: number) => void;
  lockShed: () => void;
  unlockShed: () => void;
  setDragging: (value: boolean) => void;
  setRotating: (value: boolean) => void;
  resetCalibration: () => void;
  calculateScaleFactor: (realAreaSqFt: number) => number | null;
}

const CalibrationContext = createContext<CalibrationContextValue | null>(null);

const initialState: CalibrationState = {
  isSurveyLoaded: false,
  surveyType: null,
  surveyData: null,
  surveyFile: null,
  currentMode: MODES.SELECT,
  calibPoints: { ...INITIAL_CALIBRATION_POINTS },
  scaleFactor: null,
  isCalibrationComplete: false,
  nextCornerIndex: 0,
  allCornersMarked: false,
  shedPosition: { x: 0, y: 0 },
  shedRotation: 0,
  isShedLocked: false,
  isShedPlaced: false,
  isDragging: false,
  isRotating: false,
};

function calibrationReducer(state: CalibrationState, action: CalibrationAction): CalibrationState {
  switch (action.type) {
    case "LOAD_SURVEY":
      return {
        ...state,
        isSurveyLoaded: true,
        surveyType: action.surveyType,
        surveyData: action.surveyData,
        surveyFile: action.file,
      };

    case "CLEAR_SURVEY":
      return {
        ...state,
        isSurveyLoaded: false,
        surveyType: null,
        surveyData: null,
        surveyFile: null,
        calibPoints: { ...INITIAL_CALIBRATION_POINTS },
        scaleFactor: null,
        isCalibrationComplete: false,
        isShedPlaced: false,
        isShedLocked: false,
      };

    case "SET_MODE":
      return { ...state, currentMode: action.mode };

    case "START_CALIBRATION":
      return {
        ...state,
        currentMode: MODES.CALIBRATE,
        calibPoints: { ...INITIAL_CALIBRATION_POINTS },
        isCalibrationComplete: false,
      };

    case "START_MARK_CORNERS":
      return {
        ...state,
        currentMode: MODES.MARK_CORNERS,
        calibPoints: { ...INITIAL_CALIBRATION_POINTS },
        nextCornerIndex: 0,
        allCornersMarked: false,
        isCalibrationComplete: false,
      };

    case "ADD_CORNER_POINT": {
      const currentCorner = CORNER_ORDER[state.nextCornerIndex];
      const newCalibPoints = {
        ...state.calibPoints,
        [currentCorner]: action.point,
      };
      const newIndex = state.nextCornerIndex + 1;
      const allMarked = newIndex >= CORNER_ORDER.length;

      return {
        ...state,
        calibPoints: newCalibPoints,
        nextCornerIndex: allMarked ? state.nextCornerIndex : newIndex,
        allCornersMarked: allMarked,
      };
    }

    case "SET_CALIB_POINT":
      return {
        ...state,
        calibPoints: {
          ...state.calibPoints,
          [action.position]: action.point,
        },
      };

    case "SET_ALL_CALIB_POINTS":
      return {
        ...state,
        calibPoints: action.points,
      };

    case "COMPLETE_CALIBRATION":
      return {
        ...state,
        scaleFactor: action.scaleFactor,
        isCalibrationComplete: true,
        currentMode: MODES.SELECT,
      };

    case "START_SHED_PLACEMENT":
      return {
        ...state,
        isShedPlaced: true,
        isShedLocked: false,
        currentMode: MODES.SELECT,
      };

    case "SET_SHED_POSITION":
      return { ...state, shedPosition: action.position };

    case "SET_SHED_ROTATION":
      return { ...state, shedRotation: action.rotation };

    case "LOCK_SHED":
      return { ...state, isShedLocked: true };

    case "UNLOCK_SHED":
      return { ...state, isShedLocked: false };

    case "SET_DRAGGING":
      return { ...state, isDragging: action.value };

    case "SET_ROTATING":
      return { ...state, isRotating: action.value };

    case "RESET_CALIBRATION":
      return {
        ...state,
        calibPoints: { ...INITIAL_CALIBRATION_POINTS },
        scaleFactor: null,
        isCalibrationComplete: false,
        isShedPlaced: false,
        isShedLocked: false,
        shedPosition: { x: 0, y: 0 },
        shedRotation: 0,
      };

    default:
      return state;
  }
}

export function CalibrationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(calibrationReducer, initialState);

  const imageViewerRef = useRef<HTMLImageElement | null>(null);
  const pdfViewerRef = useRef<HTMLCanvasElement | null>(null);
  const surveyContainerRef = useRef<HTMLDivElement | null>(null);
  const calibrationSvgRef = useRef<SVGSVGElement | null>(null);
  const shedOverlayRef = useRef<HTMLDivElement | null>(null);

  const loadSurvey = useCallback(
    (surveyType: "image" | "pdf", surveyData: string | ArrayBuffer, file: File) => {
      dispatch({ type: "LOAD_SURVEY", surveyType, surveyData, file });
    },
    [],
  );

  const clearSurvey = useCallback(() => {
    dispatch({ type: "CLEAR_SURVEY" });
  }, []);

  const setMode = useCallback((mode: CalibrationMode) => {
    dispatch({ type: "SET_MODE", mode });
  }, []);

  const startCalibration = useCallback(() => {
    dispatch({ type: "START_CALIBRATION" });
  }, []);

  const startMarkCorners = useCallback(() => {
    dispatch({ type: "START_MARK_CORNERS" });
  }, []);

  const addCornerPoint = useCallback((point: Point) => {
    dispatch({ type: "ADD_CORNER_POINT", point });
  }, []);

  const setCalibPoint = useCallback((position: string, point: Point) => {
    dispatch({ type: "SET_CALIB_POINT", position, point });
  }, []);

  const setAllCalibPoints = useCallback((points: CalibrationPoints) => {
    dispatch({ type: "SET_ALL_CALIB_POINTS", points });
  }, []);

  const completeCalibration = useCallback((scaleFactor: number) => {
    dispatch({ type: "COMPLETE_CALIBRATION", scaleFactor });
  }, []);

  const startShedPlacement = useCallback(() => {
    dispatch({ type: "START_SHED_PLACEMENT" });
  }, []);

  const setShedPosition = useCallback((position: Point) => {
    dispatch({ type: "SET_SHED_POSITION", position });
  }, []);

  const setShedRotation = useCallback((rotation: number) => {
    dispatch({ type: "SET_SHED_ROTATION", rotation });
  }, []);

  const lockShed = useCallback(() => {
    dispatch({ type: "LOCK_SHED" });
  }, []);

  const unlockShed = useCallback(() => {
    dispatch({ type: "UNLOCK_SHED" });
  }, []);

  const setDragging = useCallback((value: boolean) => {
    dispatch({ type: "SET_DRAGGING", value });
  }, []);

  const setRotating = useCallback((value: boolean) => {
    dispatch({ type: "SET_ROTATING", value });
  }, []);

  const resetCalibration = useCallback(() => {
    dispatch({ type: "RESET_CALIBRATION" });
  }, []);

  const calculateScaleFactor = useCallback(
    (realAreaSqFt: number) => {
      const { tl, tr, br, bl } = state.calibPoints;
      if (!tl || !tr || !br || !bl) return null;

      const pts = [tl, tr, br, bl];
      let sum1 = 0,
        sum2 = 0;
      for (let i = 0; i < pts.length; i++) {
        const next = (i + 1) % pts.length;
        sum1 += pts[i].x * pts[next].y;
        sum2 += pts[i].y * pts[next].x;
      }
      const pixelArea = Math.abs((sum1 - sum2) / 2);

      if (pixelArea > 0 && realAreaSqFt > 0) {
        return Math.sqrt(realAreaSqFt / pixelArea);
      }
      return null;
    },
    [state.calibPoints],
  );

  const getNextCornerName = useCallback(() => {
    const names: Record<string, string> = {
      tl: "Top-Left",
      tr: "Top-Right",
      br: "Bottom-Right",
      bl: "Bottom-Left",
    };
    return names[CORNER_ORDER[state.nextCornerIndex]] || "";
  }, [state.nextCornerIndex]);

  const value: CalibrationContextValue = {
    ...state,
    imageViewerRef,
    pdfViewerRef,
    surveyContainerRef,
    calibrationSvgRef,
    shedOverlayRef,
    cornerOrder: CORNER_ORDER,
    getNextCornerName,
    loadSurvey,
    clearSurvey,
    setMode,
    startCalibration,
    startMarkCorners,
    addCornerPoint,
    setCalibPoint,
    setAllCalibPoints,
    completeCalibration,
    startShedPlacement,
    setShedPosition,
    setShedRotation,
    lockShed,
    unlockShed,
    setDragging,
    setRotating,
    resetCalibration,
    calculateScaleFactor,
  };

  return (
    <CalibrationContext.Provider value={value}>{children}</CalibrationContext.Provider>
  );
}

export function useCalibration(): CalibrationContextValue {
  const context = useContext(CalibrationContext);
  if (!context) {
    throw new Error("useCalibration must be used within a CalibrationProvider");
  }
  return context;
}

export default CalibrationContext;
